import React from 'react';
import { Immutable, toImmutable } from 'nuclear-js';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import Reactor from 'data/reactor';
import DataUtils from 'data/utils';
import StudentGetters from 'data/student/getters';
import RuntimeGetters from 'data/app-runtime/getters';
import ProficiencyUtils from 'utils/proficiency';
import StudentUtils from 'utils/students';
import { DeepCompareComponentShouldUpdateMixin } from 'utils/react';

import comparisonsUtils from './utils';
import CommonValues from 'resources/common-values';
import NormalContent from './normal';
import ExpandedContent from './expanded/index';

export default CreateReactClass({
    displayName: 'ComparisonsContainer',
    mixins: [DeepCompareComponentShouldUpdateMixin, Reactor.ReactMixin],

    propTypes: {
        studentBid: PropTypes.string.isRequired,
        subject: PropTypes.instanceOf(Immutable.Map).isRequired,
        selectedTermNumber: PropTypes.number.isRequired,
        studentTermData: PropTypes.instanceOf(Immutable.List).isRequired,
        testEvents: PropTypes.instanceOf(Immutable.List).isRequired,
        mode: PropTypes.oneOf(['normal', 'expanded']),
        batchInfo: PropTypes.object /* Only exists when in Batch Print mode */,
        selectedSubject: PropTypes.instanceOf(Immutable.Map),
        keystoneTestEvent: PropTypes.object,
    },

    getDataBindings() {
        return {
            comparisonGrowthPeriods: StudentGetters.growthPercentilesForKeystoneTestEvent,
            selectedComparisonPeriod: RuntimeGetters.selectedComparisonPeriod(this.props.subject.get('bid')),
        };
    },

    getDefaultSelectedComparisonPeriod(dropDownPeriods) {
        const currentlySelectedPeriod = this.state.selectedComparisonPeriod;
        return currentlySelectedPeriod
            ? currentlySelectedPeriod
            : dropDownPeriods && dropDownPeriods.get('growthPeriods') && dropDownPeriods.get('growthPeriods').size > 0
            ? dropDownPeriods.get('growthPeriods').get(0)
            : null;
    },

    getDataForModule() {
        let proficienciesToDisplay = null;
        let getProficienciesToDisplay = null;
        let studentGrade = 0;
        let statusPercentile = null;
        let lexileRange = null;
        let quantileRange = null;
        let comparisonDropdownPeriods = null;
        let isUserNormData = false;
        if (this.props.keystoneTestEvent) {
            isUserNormData = DataUtils.isUserNormData(this.props.keystoneTestEvent);
            // TODO:CSN Make sure we test proficiencies
            getProficienciesToDisplay = ProficiencyUtils.getDisplayProficiencies(
                this.props.keystoneTestEvent,
                this.props.subject.get('bid')
            );
            studentGrade = parseInt(this.props.keystoneTestEvent.get('gradeName', 0));
            statusPercentile = this.props.keystoneTestEvent.getIn(['statusNorms', 'studentPercentile'], null);
            // TODO:CSN - what are the requirements for lexile and quantile ranges with CSTE
            if (this.props.subject.get('bid') === CommonValues.SUBJECT_BID_READING) {
                lexileRange = comparisonsUtils.getLexileRange(this.props.keystoneTestEvent);
            }
            if (this.props.subject.get('bid') === CommonValues.SUBJECT_BID_MATHEMATICS) {
                quantileRange = comparisonsUtils.getQuantileRange(this.props.keystoneTestEvent);
            }

            comparisonDropdownPeriods = comparisonsUtils.getComparisonDropdownPeriods(
                this.props.keystoneTestEvent.get('measurementScaleID'),
                this.props.selectedTermNumber,
                this.props.testEvents.valueSeq(),
                this.state.comparisonGrowthPeriods,
                this.props.studentTermData
            );
        }
        if (__BATCH__ && getProficienciesToDisplay) {
            const { selectedLinkingStudies } = this.props.batchInfo;
            proficienciesToDisplay = toImmutable(
                getProficienciesToDisplay.reduce((acc, study) => {
                    if (selectedLinkingStudies.includes(study.get('study').toLowerCase())) {
                        acc.push(study);
                    }
                    return acc;
                }, [])
            );
        } else {
            proficienciesToDisplay = getProficienciesToDisplay;
        }

        return {
            testEvent: this.props.keystoneTestEvent,
            statusPercentile,
            proficienciesToDisplay,
            lexileRange,
            quantileRange,
            studentGrade,
            comparisonDropdownPeriods,
            isUserNormData,
        };
    },

    render() {
        const {
            testEvent,
            statusPercentile,
            proficienciesToDisplay,
            lexileRange,
            quantileRange,
            studentGrade,
            comparisonDropdownPeriods,
            isUserNormData,
        } = this.getDataForModule();
        if (this.props.mode === 'normal') {
            return (
                <NormalContent
                    key="comparisons-default-content"
                    currentTestEvent={testEvent}
                    lexileRange={lexileRange}
                    quantileRange={quantileRange}
                    proficienciesToDisplay={proficienciesToDisplay}
                    statusPercentile={statusPercentile}
                    studentBid={this.props.studentBid}
                    subjectBid={this.props.subject.get('bid')}
                    comparisonDropdownPeriods={comparisonDropdownPeriods}
                    defaultComparisonPeriodFunction={this.getDefaultSelectedComparisonPeriod}
                    selectedComparisonPeriod={this.state.selectedComparisonPeriod}
                    isUserNormData={isUserNormData}
                />
            );
        } else if (!__BATCH__ || (this.props.batchInfo && this.props.batchInfo.batchComparisonsRotation)) {
            if (Reactor.evaluate(StudentGetters.studentExistsInStore(this.props.studentBid))) {
                //Gender may be null for some students as the field is not required.
                const gender = StudentUtils.getStudentGender(this.props.studentBid);
                const firstName = StudentUtils.getStudentFirstName(this.props.studentBid);
                return (
                    <ExpandedContent
                        key="comparisons-expanded-module"
                        currentTestEvent={testEvent}
                        selectedSubject={this.props.selectedSubject}
                        lexileRange={lexileRange}
                        quantileRange={quantileRange}
                        proficienciesToDisplay={proficienciesToDisplay}
                        studentBid={this.props.studentBid}
                        studentGrade={studentGrade}
                        studentGender={gender === null || gender === undefined ? 'undefined' : gender}
                        studentFirstName={firstName}
                        subjectBid={this.props.subject.get('bid')}
                        comparisonDropdownPeriods={comparisonDropdownPeriods}
                        defaultComparisonPeriodFunction={this.getDefaultSelectedComparisonPeriod}
                        selectedComparisonPeriod={this.state.selectedComparisonPeriod}
                        isUserNormData={isUserNormData}
                        batchInfo={this.props.batchInfo}
                        keystoneTestEvent={this.props.keystoneTestEvent}
                    />
                );
            }
            return null;
        } else {
            // Don't include xComparisons if batch print doesn't require it.
            return null;
        }
    },
});
